import { DashboardWrapper } from '@/components/DashboardWrapper';
import PageContainer from '@/components/PageContainer';
import { Auth, IUserContext, useUserContext } from '@/context/UserContext';
import { Client } from '@types';
import { useEffect, useMemo, useState } from 'react';
import { get } from '@/utils/queries';
import {
  generateAmoebaUrlWithToken,
  generateChatbotNewUrlWithoutKey,
} from '@/pages/Chatbot/utils';
import { alertErrorMessage } from '@/utils/alerts';
import { Spinner } from '@/components/Spinner';
import { Navigate } from 'react-router-dom';
import { getDefaultRoute } from '@/utils';
import { isChatbotAllowed } from '@/components/Nav/utils';

export function Chatbot() {
  const {
    auth,
    impersonatingAccountId,
    companyName,
    companyChatbotEnabled,
  }: IUserContext = useUserContext(); // Use the context
  const { data: companyData } = auth?.isClient
    ? get<Client[]>(`account/${auth.clients[0].id}`)
    : get<Client[]>('account/list');
  const [src, setSrc] = useState('');
  const [css, setCss] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isChatbotEnabled, setIsChatbotEnabled] = useState<boolean | null>(
    null
  );

  const clientTeams = useMemo(() => {
    const isPreProductionEnv =
      import.meta.env?.VITE_ENV === 'local' ||
      import.meta.env?.VITE_ENV === 'test' ||
      process.env?.VITE_ENV === 'local' ||
      process.env?.VITE_ENV === 'test';
    if (isPreProductionEnv) {
      return import.meta.env.VITE_AMOEBA_TEAM_ID as string;
    }
    if (auth?.isClient) {
      return auth?.clients?.[0]?.ms_team_id;
    }
    if (impersonatingAccountId) {
      const client = companyData?.find((c) => c.id === impersonatingAccountId);
      return client?.ms_team_id;
    }
    return '';
  }, [auth, impersonatingAccountId, companyData]);

  const props = {
    ...(companyName && { companyName }),
    ...(companyData && { companyData }),
  };

  useEffect(() => {
    (async () => {
      const url = await generateAmoebaUrlWithToken([clientTeams as string]);
      setSrc(url);
      // setCss('hidden');
    })().catch(() => {
      alertErrorMessage(
        'Error loading chatbot, please refresh the page or contact support.'
      );
    });
  }, [clientTeams]);

  useEffect(() => {
    return () => {
      document.cookie =
        'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    };
  }, []);

  const isReady = auth !== undefined && companyChatbotEnabled !== undefined;

  useEffect(() => {
    if (!isReady) return;

    const result = isChatbotAllowed(
      auth,
      companyChatbotEnabled,
      auth?.isClient ? auth?.clients?.[0]?.id : impersonatingAccountId
    );

    setIsChatbotEnabled(result);
  }, [isReady, auth, companyChatbotEnabled, impersonatingAccountId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://financial-analyst.app.opstart.co') {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { data } = event;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if ((data?.type as string) === 'AMOEBA_WIDGET_READY') {
        setCss('w-full h-full bg-transparent border-none');
        setIsLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  if (isChatbotEnabled === false) {
    return (
      <Navigate
        to={getDefaultRoute(auth as Auth, Boolean(impersonatingAccountId))}
        replace
      />
    );
  }

  return (
    <PageContainer name='files'>
      <DashboardWrapper {...props}>
        <div />
        {isLoading ? (
          <Spinner className='opacity-50 mt-20 w-[30%] justify-self-center' />
        ) : null}
        <iframe
          title='Embedded chatbox'
          className={css}
          src={src}
          // eslint-disable-next-line react/no-unknown-property
          onLoadedData={() => setSrc(generateChatbotNewUrlWithoutKey())}
        />
      </DashboardWrapper>
    </PageContainer>
  );
}
